import React, { Component } from 'react'
import { createPortal } from 'react-dom'
import { withTranslation } from 'react-i18next'
import _ContactUs_Button from './_styles/_ContactUs_Button'
import _ContactUs_Modal from './_styles/_ContactUs_Modal'
import Stripes from 'components/Stripes/Stripes'
import ContactUsModalContent from './ContactUsModalContent'

// Define a movement threshold in pixels to distinguish between shaky clicks and intentional drags
const MOVEMENT_THRESHOLD = 5

// этот компонент является реакт виджетом
// если будете ему добавлять пропсы, не забывайте добавить их в реакт виджет тоже
@withTranslation()
export default class ContactUsButton extends Component {
  state = {
    isModalOpen: false,
    position: {
      right: 40,  // Default values from CSS
      bottom: 80,
    },
    isMobile: false,
    bottomDockOffset: 0,
    isDragging: false,
    dragStartPos: { x: 0, y: 0 },
    dragStarted: false,
    // Track the actual movement distance
    totalMovement: 0
  }

  modalRef = React.createRef()
  buttonRef = React.createRef()

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
    document.addEventListener('touchstart', this.handleClickOutside)
    document.addEventListener('mousemove', this.handleMouseMove)
    document.addEventListener('mouseup', this.handleMouseUp)
    document.addEventListener('keydown', this.handleKeyDown)
    window.addEventListener('resize', this.scheduleViewportUpdate)
    window.addEventListener('scroll', this.scheduleViewportUpdate, true)

    this.viewportObserver = new MutationObserver(this.scheduleViewportUpdate)
    this.viewportObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true
    })

    // Load saved position from localStorage if available
    const savedPosition = localStorage.getItem('contactUsButtonPosition')
    if (savedPosition) {
      try {
        const position = JSON.parse(savedPosition)
        this.setState({ position })
      } catch (e) {
        console.error('Failed to parse saved position', e)
      }
    }

    this.updateViewportState()
    if (this.props.initiallyOpen) this.setState({ isModalOpen: true })
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
    document.removeEventListener('touchstart', this.handleClickOutside)
    document.removeEventListener('mousemove', this.handleMouseMove)
    document.removeEventListener('mouseup', this.handleMouseUp)
    document.removeEventListener('keydown', this.handleKeyDown)
    window.removeEventListener('resize', this.scheduleViewportUpdate)
    window.removeEventListener('scroll', this.scheduleViewportUpdate, true)

    if (this.viewportObserver) {
      this.viewportObserver.disconnect()
    }

    if (this.viewportUpdateRaf) {
      cancelAnimationFrame(this.viewportUpdateRaf)
    }
  }

  handleClickOutside = (event) => {
    if (
      this.state.isModalOpen &&
      this.modalRef.current &&
      !this.modalRef.current.contains(event.target) &&
      !event.target.closest('._ContactUs_Button')) {
      this.setState({ isModalOpen: false })
    }
  }

  handleMouseDown = (event) => {
    if (this.state.isMobile) {
      return
    }

    // Prevent default to avoid text selection
    event.preventDefault()

    // Stop event propagation to prevent toggleModal from firing
    event.stopPropagation()

    this.setState({
      isDragging: true,
      dragStarted: false,
      dragStartPos: {
        x: event.clientX,
        y: event.clientY
      },
      totalMovement: 0
    })
  }

  handleMouseMove = (event) => {
    if (this.state.isMobile || !this.state.isDragging) return

    // Calculate how far we've moved from the starting drag position
    const deltaX = this.state.dragStartPos.x - event.clientX
    const deltaY = this.state.dragStartPos.y - event.clientY

    // Calculate the absolute movement distance (Euclidean distance)
    const movement = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

    // Update total movement
    const newTotalMovement = this.state.totalMovement + movement

    // Mark as dragging only if we've exceeded the threshold
    const dragStarted = newTotalMovement > MOVEMENT_THRESHOLD

    // Update the position based on the mouse movement
    this.setState(prevState => ({
      position: {
        right: prevState.position.right + deltaX,
        bottom: prevState.position.bottom + deltaY
      },
      dragStartPos: {
        x: event.clientX,
        y: event.clientY
      },
      dragStarted: dragStarted || prevState.dragStarted,
      totalMovement: newTotalMovement
    }))
  }

  handleMouseUp = () => {
    if (this.state.isMobile) {
      return
    }

    // If we were dragging and actually moved the element
    if (this.state.isDragging) {
      // Save the final position to localStorage only if we actually dragged
      if (this.state.dragStarted) {
        localStorage.setItem('contactUsButtonPosition', JSON.stringify(this.state.position))
      }

      this.suppressClick = this.state.dragStarted

      this.setState({
        isDragging: false,
        dragStarted: false,
        totalMovement: 0
      })
    }
  }

  handleButtonClick = () => {
    if (this.suppressClick) {
      this.suppressClick = false
      return
    }
    const will_open = !this.state.isModalOpen
    this.toggleModal()
    if (will_open && this.props.onOpen) this.props.onOpen()
  }

  handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.state.isModalOpen) this.closeModal()
  }

  render () {
    if (typeof document === 'undefined') {
      return null
    }

    const { position, isDragging, isMobile, bottomDockOffset } = this.state
    const viewport_width = typeof window !== 'undefined' ? window.innerWidth : 1440
    const viewport_height = typeof window !== 'undefined' ? window.innerHeight : 900

    // Ensure position values are constrained to viewport
    const safePosition = {
      right: isMobile ? 16 : Math.max(0, Math.min(viewport_width - 100, position.right)),
      bottom: isMobile
        ? (16 + bottomDockOffset)
        : Math.max(0, Math.min(viewport_height - 50, position.bottom))
    }

    const use_mobile_top = isMobile && this.props.mobileTop && !this.state.isModalOpen
    const buttonStyle = {
      right: `${safePosition.right}px`,
      bottom: use_mobile_top ? 'auto' : `${safePosition.bottom}px`,
      top: use_mobile_top ? '94px' : 'auto',
      cursor: isMobile ? 'pointer' : (isDragging ? 'grabbing' : 'grab'),
      userSelect: 'none'
    }

    const content = (
      <>
        <_ContactUs_Button
          open={this.state.isModalOpen}
          isMobile={isMobile}
          className="_ContactUs_Button"
          style={buttonStyle}
          ref={this.buttonRef}
          onMouseDown={this.handleMouseDown}
          onClick={this.handleButtonClick}
          type="button"
          aria-label={this.props.t('Contact us')}
          title={this.props.t('Contact us')}
        >
          <div className='contact-us-button-icon'>
            <Stripes size={0.70} />
          </div>
          {!isMobile && <span className='contact-us-button-text'>{this.props.t('Contact us')}</span>}
        </_ContactUs_Button>

        <_ContactUs_Modal
          open={this.state.isModalOpen}
          isMobile={isMobile}
          className="_ContactUs_Modal"
          ref={this.modalRef}
          style={{
            right: `${safePosition.right}px`,
            bottom: `${safePosition.bottom + (isMobile ? 60 : 70)}px`
          }}
        >
          <ContactUsModalContent closeModal={this.closeModal} />
        </_ContactUs_Modal>
      </>
    )

    const portalTarget = document.getElementById('overflow-root') || document.body

    return createPortal(content, portalTarget)
  }

  toggleModal = () => {
    this.setState((prevState) => ({
      isModalOpen: !prevState.isModalOpen,
    }))
  }

  closeModal = () => {
    this.setState({ isModalOpen: false }, () => {
      if (this.buttonRef.current) this.buttonRef.current.focus()
    })
  }

  scheduleViewportUpdate = () => {
    if (this.viewportUpdateRaf) {
      cancelAnimationFrame(this.viewportUpdateRaf)
    }

    this.viewportUpdateRaf = requestAnimationFrame(() => {
      this.viewportUpdateRaf = null
      this.updateViewportState()
    })
  }

  updateViewportState = () => {
    const isMobile = window.innerWidth < 945
    const bottomDockOffset = isMobile ? this.getBottomDockOffset() : 0

    this.setState((prevState) => {
      if (prevState.isMobile === isMobile && prevState.bottomDockOffset === bottomDockOffset) {
        return null
      }

      return {
        isMobile,
        bottomDockOffset
      }
    })
  }

  getBottomDockOffset = () => {
    const elements = Array.from(document.body.querySelectorAll('*'))
    let maxOffset = 0

    elements.forEach((element) => {
      if (
        element.closest('._ContactUs_Button')
        || element.closest('._ContactUs_Modal')
      ) {
        return
      }

      const styles = window.getComputedStyle(element)
      if (styles.display === 'none' || styles.visibility === 'hidden' || Number(styles.opacity) === 0) {
        return
      }

      if (!['fixed', 'sticky'].includes(styles.position)) {
        return
      }

      const rect = element.getBoundingClientRect()
      const isBottomDocked =
        rect.height >= 48
        && rect.width >= Math.min(window.innerWidth * 0.6, 280)
        && rect.bottom >= window.innerHeight - 2
        && rect.top < window.innerHeight

      if (!isBottomDocked) {
        return
      }

      maxOffset = Math.max(maxOffset, Math.ceil(window.innerHeight - rect.top))
    })

    return Math.min(maxOffset, 120)
  }
}
